/**
 * Cashflow projection API (TMMAPP-0010 / TMMAPP-0011, design §8.9 / §10.8) — wire types from
 * `src/api/generated/`; normalized into the module's domain type. `points` / `series` are
 * sorted date-ascending. Optional wire fields become `null`; an event or register row of a kind
 * this app doesn't know yet is dropped.
 */
import { request } from './client';
import type {
  AccountProjection as WireAccountProjection,
  CashflowPlan as WireCashflowPlan,
  CombinedCashflow as WireCombinedCashflow,
  DiffersFromSeries as WireDiffersFromSeries,
  EventView as WireEventView,
  ForecastSliceRule as WireForecastSliceRule,
  FxUnavailable as WireFxUnavailable,
  Minimum as WireMinimum,
  MinimumCause as WireMinimumCause,
  NamedSeries as WireNamedSeries,
  PlanSliceRow as WirePlanSliceRow,
  Point as WirePoint,
  RegisterRow as WireRegisterRow,
  EnvelopeProjection as WireEnvelopeProjection,
} from './generated';
import type { OccurrenceDiffers, OccurrenceState } from './planned';

export interface ProjectionWindow {
  from?: string | undefined;
  to?: string | undefined;
}

export type EventKind = 'OPENING' | 'OCCURRENCE' | 'SLICE' | 'PLANNED_EXPENSE';
export type RegisterKind = EventKind;
export type PlanLineKind = 'INCOME' | 'EXPENSE';

export interface Point {
  date: string;
  balanceMinor: number;
  unallocatedMinor: number | null;
}

export interface MinimumCause {
  amountMinor: number;
  /** `null` for a kind this app doesn't know yet. */
  kind: EventKind | null;
  occursOn: string;
  payee: string | null;
  planLineId: number | null;
  seriesId: number | null;
  envelopeId: number | null;
}

export interface Minimum {
  date: string;
  balanceMinor: number;
  /** Why the minimum landed there; null when the wire omits it. */
  cause: MinimumCause | null;
}

export interface FxUnavailable {
  base: string;
  quote: string;
  omittedSlices: number;
}

export interface EventView {
  kind: EventKind;
  occursOn: string;
  seriesId: number | null;
  payee: string | null;
  amountMinor: number;
  runningMinor: number;
  state: OccurrenceState | null;
  envelopeId: number | null;
  pending: boolean | null;
  allocatedMinor: number | null;
  freeMinor: number | null;
  runningUnallocatedMinor: number | null;
  /** An unresolved past one-off drawn at today (TMMAPI-0148). */
  overdue: boolean;
}

export interface RegisterRow {
  kind: RegisterKind;
  date: string;
  balanceMinor: number | null;
  runningBalanceMinor: number;
  pending: boolean | null;
  occursOn: string | null;
  envelopeId: number | null;
  envelopeName: string | null;
  /** A slice aimed at the virtual account's Unassigned (TMMAPI-0145). */
  unassigned: boolean;
  amountMinor: number | null;
  categoryId: number | null;
  state: OccurrenceState | null;
  differsFromSeries: OccurrenceDiffers | null;
  seriesId: number | null;
  seriesTargetId: number | null;
  sliceRef: string | null;
  originalOn: string | null;
  payee: string | null;
  remainderMinor: number | null;
  partiallyExecuted: boolean | null;
  planLineId: number | null;
  planLineKind: PlanLineKind | null;
  note: string | null;
  spreadGroup: string | null;
  /** Opaque, response-scoped (TMMAPI-0143): an occurrence and its slices share it. */
  eventKey: string | null;
  /** Anything differs from the series (TMMAPI-0143, owner only); null when not served. */
  edited: boolean | null;
  /** Where the row comes from (TMMAPI-0143, published); null when absent (no envelope target). */
  sourceKind: ForecastSourceKind | null;
  /** The slice's source account name (TMMAPI-0143, owner only). */
  sourceAccountName: string | null;
  /** An unresolved past one-off drawn at today (TMMAPI-0148, MM-D-122); `originalOn` is its own date. */
  overdue: boolean;
  /** The split rule that projects this slice out of Unassigned (TMMAPI-0148, MM-D-123). */
  splitRuleId: number | null;
  /** The slice's split rule (TMMAPI-0143, owner only). */
  rule: ForecastSliceRule | null;
  /**
   * Opaque, response-scoped (TMMAPI-0202, MM-D-154; published): every date of one series slice
   * shares it, so "Coming from your accounts" groups by it. Never a series id.
   */
  sliceKey: string | null;
}

export type ForecastSourceKind = 'ALLOCATED' | 'BUDGET_PLAN' | 'ENVELOPE' | 'ONE_TIME';
export type ForecastSliceRuleMode = 'FIXED' | 'PERCENT' | 'REMAINDER';

export interface ForecastSliceRule {
  mode: ForecastSliceRuleMode;
  /** FIXED only. */
  amountMinor: number | null;
  /** PERCENT only, in basis points. */
  bp: number | null;
}

export interface NamedSeries {
  envelopeId: number | null;
  accountId: number | null;
  /** The virtual account's Unassigned series (TMMAPI-0145); its name is served. */
  unassigned: boolean;
  name: string;
  points: Point[];
  minimum: Minimum;
  endMinor: number;
  trend: number;
}

export interface AccountProjection {
  from: string;
  to: string;
  /**
   * The window tmm-api used (TMMAPI-0188 U3, a forgiving window): a past `from` is read as today, so
   * `windowFrom` may start later than asked; `windowTo` before it marks a wholly past window.
   */
  windowFrom: string;
  windowTo: string;
  accountId: number;
  accountName: string;
  series: Point[];
  minimum: Minimum;
  endMinor: number;
  trend: number;
  events: EventView[];
  register: RegisterRow[];
  endUnallocatedMinor: number | null;
  fxUnavailable: FxUnavailable[];
}

export interface EnvelopeProjection {
  from: string;
  to: string;
  /** The window tmm-api used, as on `AccountProjection`. */
  windowFrom: string;
  windowTo: string;
  envelopeId: number;
  series: Point[];
  minimum: Minimum;
  endMinor: number;
  trend: number;
  events: EventView[];
  register: RegisterRow[];
  fxUnavailable: FxUnavailable[];
}

export interface CombinedCashflow {
  from: string;
  to: string;
  /** The window tmm-api used, as on `AccountProjection`. */
  windowFrom: string;
  windowTo: string;
  accounts: AccountProjection[];
  combined: NamedSeries;
  events: EventView[];
  register: RegisterRow[];
  fxUnavailable: FxUnavailable[];
  /** The selected accounts counted from 0 because their opening is pending (TMMAPI-0160 U2). */
  openingPendingAccountIds: number[];
}

export interface PlanSliceRow {
  seriesId: number | null;
  seriesTargetId: number | null;
  sliceRef: string | null;
  originalOn: string | null;
  occursOn: string;
  payee: string | null;
  remainderMinor: number | null;
  partiallyExecuted: boolean | null;
  amountMinor: number;
  categoryId: number | null;
  differsFromSeries: OccurrenceDiffers | null;
  pending: boolean | null;
  state: OccurrenceState | null;
  envelopeId: number | null;
}

export interface CashflowPlan {
  from: string;
  to: string;
  /** The window tmm-api used, as on `AccountProjection`. */
  windowFrom: string;
  windowTo: string;
  series: NamedSeries[];
  slices: PlanSliceRow[];
  events: EventView[];
  register: RegisterRow[];
  fxUnavailable: FxUnavailable[];
}

const EVENT_KINDS: ReadonlySet<string> = new Set([
  'OPENING',
  'OCCURRENCE',
  'SLICE',
  'PLANNED_EXPENSE',
]);
const STATES: ReadonlySet<string> = new Set([
  'PLANNED',
  'PARTIAL',
  'MATCHED',
  'POSTED',
  'MISSED',
  'SUPERSEDED',
  'CANCELLED',
]);

function byDate<T extends { date: string }>(a: T, b: T): number {
  return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
}

export function normalizePoint(w: WirePoint): Point {
  return {
    date: w.date,
    balanceMinor: w.balanceMinor,
    unallocatedMinor: w.unallocatedMinor ?? null,
  };
}

export function normalizePoints(value: readonly WirePoint[]): Point[] {
  return value.map(normalizePoint).sort(byDate);
}

function normalizeKind(value: string): EventKind | null {
  return EVENT_KINDS.has(value) ? (value as EventKind) : null;
}

function normalizeState(value: string | undefined): OccurrenceState | null {
  return value !== undefined && STATES.has(value) ? (value as OccurrenceState) : null;
}

function normalizeMinimumCause(w: WireMinimumCause): MinimumCause {
  return {
    amountMinor: w.amountMinor,
    kind: normalizeKind(w.kind),
    occursOn: w.occursOn,
    payee: w.payee ?? null,
    planLineId: w.planLineId ?? null,
    seriesId: w.seriesId ?? null,
    envelopeId: w.envelopeId ?? null,
  };
}

export function normalizeMinimum(w: WireMinimum): Minimum {
  return {
    date: w.date,
    balanceMinor: w.balanceMinor,
    cause: w.cause == null ? null : normalizeMinimumCause(w.cause),
  };
}

export function normalizeFx(w: WireFxUnavailable): FxUnavailable {
  return { base: w.base, quote: w.quote, omittedSlices: w.omittedSlices };
}

function normalizeDiffers(w: WireDiffersFromSeries | undefined): OccurrenceDiffers | null {
  return w == null ? null : { date: w.date, amount: w.amount, slices: w.slices };
}

/** An event; `null` (dropped, warned in DEV) for a kind this app doesn't know yet. */
export function normalizeEvent(w: WireEventView): EventView | null {
  const kind = normalizeKind(w.kind);
  if (kind === null) {
    if (import.meta.env.DEV) console.warn('projections: event of an unknown kind dropped', w);
    return null;
  }
  return {
    kind,
    occursOn: w.occursOn,
    seriesId: w.seriesId ?? null,
    payee: w.payee ?? null,
    amountMinor: w.amountMinor,
    runningMinor: w.runningMinor,
    state: normalizeState(w.state),
    envelopeId: w.envelopeId ?? null,
    pending: w.pending ?? null,
    allocatedMinor: w.allocatedMinor ?? null,
    freeMinor: w.freeMinor ?? null,
    runningUnallocatedMinor: w.runningUnallocatedMinor ?? null,
    overdue: w.overdue ?? false,
  };
}

function normalizeEvents(value: readonly WireEventView[]): EventView[] {
  return value.flatMap((row) => {
    const event = normalizeEvent(row);
    return event ? [event] : [];
  });
}

/** A register row; `null` (dropped, warned in DEV) for a kind this app doesn't know yet. */
export function normalizeRegisterRow(w: WireRegisterRow): RegisterRow | null {
  const kind = normalizeKind(w.kind);
  if (kind === null) {
    if (import.meta.env.DEV)
      console.warn('projections: register row of an unknown kind dropped', w);
    return null;
  }
  return {
    kind,
    date: w.date,
    balanceMinor: w.balanceMinor ?? null,
    runningBalanceMinor: w.runningBalanceMinor,
    pending: w.pending ?? null,
    occursOn: w.occursOn ?? null,
    envelopeId: w.envelopeId ?? null,
    envelopeName: w.envelopeName ?? null,
    unassigned: w.unassigned ?? false,
    amountMinor: w.amountMinor ?? null,
    categoryId: w.categoryId ?? null,
    state: normalizeState(w.state),
    differsFromSeries: normalizeDiffers(w.differsFromSeries),
    seriesId: w.seriesId ?? null,
    seriesTargetId: w.seriesTargetId ?? null,
    sliceRef: w.sliceRef ?? null,
    originalOn: w.originalOn ?? null,
    payee: w.payee ?? null,
    remainderMinor: w.remainderMinor ?? null,
    partiallyExecuted: w.partiallyExecuted ?? null,
    planLineId: w.planLineId ?? null,
    planLineKind:
      w.planLineKind === 'INCOME' || w.planLineKind === 'EXPENSE' ? w.planLineKind : null,
    note: w.note ?? null,
    spreadGroup: w.spreadGroup ?? null,
    eventKey: w.eventKey ?? null,
    edited: w.edited ?? null,
    sourceKind: w.sourceKind !== undefined && SOURCE_KINDS.has(w.sourceKind) ? w.sourceKind : null,
    sourceAccountName: w.sourceAccountName ?? null,
    overdue: w.overdue ?? false,
    splitRuleId: w.splitRuleId ?? null,
    rule: normalizeSliceRule(w.rule),
    sliceKey: w.sliceKey ?? null,
  };
}

const SOURCE_KINDS: ReadonlySet<string> = new Set([
  'ALLOCATED',
  'BUDGET_PLAN',
  'ENVELOPE',
  'ONE_TIME',
]);
const RULE_MODES: ReadonlySet<string> = new Set(['FIXED', 'PERCENT', 'REMAINDER']);

/** A slice rule, or null when absent or its mode is unknown. */
function normalizeSliceRule(w: WireForecastSliceRule | undefined): ForecastSliceRule | null {
  if (w == null || !RULE_MODES.has(w.mode)) return null;
  return { mode: w.mode, amountMinor: w.amountMinor ?? null, bp: w.bp ?? null };
}

function normalizeRegister(value: readonly WireRegisterRow[]): RegisterRow[] {
  return value.flatMap((row) => {
    const item = normalizeRegisterRow(row);
    return item ? [item] : [];
  });
}

export function normalizeNamedSeries(w: WireNamedSeries): NamedSeries {
  return {
    envelopeId: w.envelopeId ?? null,
    accountId: w.accountId ?? null,
    unassigned: w.unassigned ?? false,
    name: w.name,
    points: normalizePoints(w.points),
    minimum: normalizeMinimum(w.minimum),
    endMinor: w.endMinor,
    trend: w.trend,
  };
}

export function normalizeAccountProjection(w: WireAccountProjection): AccountProjection {
  return {
    from: w.from,
    to: w.to,
    windowFrom: w.windowFrom,
    windowTo: w.windowTo,
    accountId: w.accountId,
    accountName: w.accountName,
    series: normalizePoints(w.series),
    minimum: normalizeMinimum(w.minimum),
    endMinor: w.endMinor,
    trend: w.trend,
    events: normalizeEvents(w.events),
    register: normalizeRegister(w.register),
    endUnallocatedMinor: w.endUnallocatedMinor ?? null,
    fxUnavailable: (w.fxUnavailable ?? []).map(normalizeFx),
  };
}

export function normalizeEnvelopeProjection(w: WireEnvelopeProjection): EnvelopeProjection {
  return {
    from: w.from,
    to: w.to,
    windowFrom: w.windowFrom,
    windowTo: w.windowTo,
    envelopeId: w.envelopeId,
    series: normalizePoints(w.series),
    minimum: normalizeMinimum(w.minimum),
    endMinor: w.endMinor,
    trend: w.trend,
    events: normalizeEvents(w.events),
    register: normalizeRegister(w.register),
    fxUnavailable: w.fxUnavailable.map(normalizeFx),
  };
}

export function normalizeCombinedCashflow(w: WireCombinedCashflow): CombinedCashflow {
  return {
    from: w.from,
    to: w.to,
    windowFrom: w.windowFrom,
    windowTo: w.windowTo,
    accounts: w.accounts.map(normalizeAccountProjection),
    combined: normalizeNamedSeries(w.combined),
    events: normalizeEvents(w.events),
    register: normalizeRegister(w.register),
    fxUnavailable: w.fxUnavailable.map(normalizeFx),
    openingPendingAccountIds: w.openingPendingAccountIds,
  };
}

export function normalizePlanSlice(w: WirePlanSliceRow): PlanSliceRow {
  return {
    seriesId: w.seriesId ?? null,
    seriesTargetId: w.seriesTargetId ?? null,
    sliceRef: w.sliceRef ?? null,
    originalOn: w.originalOn ?? null,
    occursOn: w.occursOn,
    payee: w.payee ?? null,
    remainderMinor: w.remainderMinor ?? null,
    partiallyExecuted: w.partiallyExecuted ?? null,
    amountMinor: w.amountMinor,
    categoryId: w.categoryId ?? null,
    differsFromSeries: normalizeDiffers(w.differsFromSeries),
    pending: w.pending ?? null,
    state: normalizeState(w.state),
    envelopeId: w.envelopeId ?? null,
  };
}

export function normalizeCashflowPlan(w: WireCashflowPlan): CashflowPlan {
  return {
    from: w.from,
    to: w.to,
    windowFrom: w.windowFrom,
    windowTo: w.windowTo,
    series: w.series.map(normalizeNamedSeries),
    slices: w.slices.map(normalizePlanSlice),
    events: normalizeEvents(w.events),
    register: normalizeRegister(w.register),
    fxUnavailable: w.fxUnavailable.map(normalizeFx),
  };
}

function windowQuery(
  window: ProjectionWindow,
): Record<string, string | number | boolean | undefined> {
  return { from: window.from, to: window.to };
}

export const projectionsApi = {
  /** GET /v1/me/accounts/{accountId}/projection. */
  async account(accountId: number, window: ProjectionWindow = {}): Promise<AccountProjection> {
    return normalizeAccountProjection(
      await request<WireAccountProjection>('GET', `/v1/me/accounts/${accountId}/projection`, {
        query: windowQuery(window),
      }),
    );
  },
  /** GET /v1/envelopes/{envelopeId}/projection. */
  async envelope(envelopeId: number, window: ProjectionWindow = {}): Promise<EnvelopeProjection> {
    return normalizeEnvelopeProjection(
      await request<WireEnvelopeProjection>('GET', `/v1/envelopes/${envelopeId}/projection`, {
        query: windowQuery(window),
      }),
    );
  },
  /** GET /v1/me/cashflow. `accountIds` is comma-joined (query values are scalars). */
  async combined(window: ProjectionWindow = {}, accountIds?: number[]): Promise<CombinedCashflow> {
    return normalizeCombinedCashflow(
      await request<WireCombinedCashflow>('GET', '/v1/me/cashflow', {
        query: {
          ...windowQuery(window),
          accountIds:
            accountIds !== undefined && accountIds.length > 0 ? accountIds.join(',') : undefined,
        },
      }),
    );
  },
  /** GET /v1/virtual-accounts/{virtualAccountId}/cashflow-plan. */
  async virtualAccountPlan(
    virtualAccountId: number,
    window: ProjectionWindow = {},
    envelopeId?: number,
  ): Promise<CashflowPlan> {
    return normalizeCashflowPlan(
      await request<WireCashflowPlan>(
        'GET',
        `/v1/virtual-accounts/${virtualAccountId}/cashflow-plan`,
        {
          query: {
            ...windowQuery(window),
            envelopeId,
          },
        },
      ),
    );
  },
};
